/**
 * Runs `task` over `items` with at most `limit` in flight, settling every one
 * of them the way `Promise.allSettled` does: results come back in input order,
 * so each still names the item it came from, and one rejection neither stops
 * the rest nor rejects the whole run.
 *
 * The bound is the point. An unbounded `items.map(task)` against an HTTP API
 * hands the browser hundreds of requests to queue behind its per-host
 * connection limit: nothing goes faster, any progress readout arrives as one
 * jump at the end, and the rest of the page waits its turn behind the fan-out.
 */
export const settleWithLimit = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>
): Promise<PromiseSettledResult<R>[]> => {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      // Taking the index and advancing is a single step — workers only ever
      // interleave at an await — so no two can be handed the same item.
      const index = next++;

      try {
        results[index] = {
          status: "fulfilled",
          value: await task(items[index]),
        };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };

  // No items means no workers; anything else runs at least one, so a limit of
  // 0 or less cannot silently return a set of holes instead of results.
  const workerCount = Math.min(Math.max(limit, 1), items.length);

  // Never rejects: worker settles each task itself.
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
};
